import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { DragDropContext, Draggable, DropResult } from 'react-beautiful-dnd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { iconMapping } from 'services/utils/iconMapping';
import { faAngleLeft, faGripLines, faPen, faPlus, faTrash } from '@fortawesome/free-solid-svg-icons';
import { useModal } from 'components/modals/ModalContext';
import { useGlobalContext } from 'GlobalContext';
import { useFetchData } from 'services/api/useFetchData';
import { saveData } from 'services/api/saveData';
import { StrictModeDroppable } from 'services/utils/dragDropUtils';
import PrimaryButton from 'components/buttons/PrimaryButton';
import SecondaryButton from 'components/buttons/SecondaryButton';
import DeleteModal from 'components/modals/DeleteModal';
import KnowledgeBaseGroupForm from './KnowledgeBaseGroupForm';
import KnowledgeBaseSectionForm from './KnowledgeBaseSectionForm';
import { KnowledgeBaseArticleCard } from '../../knowledge_base/KnowledgeBaseCards';
import { KnowledgeBaseArticleListItem, KnowledgeBaseGroup, KnowledgeBaseSection, KNOWLEDGE_BASE_ADMIN_PATH, groupIconKey } from '../../knowledge_base/KnowledgeBaseTypes';
import '../../../style/scss/knowledge-base.scss';

// The droppable id of the articles without a section
const NO_SECTION_ID = 'none';

// The management view of a group, showing its sections with their articles as in the knowledge base
const KnowledgeBaseGroupAdminPage: React.FC = () => {
    const { t } = useTranslation();
    const history = useHistory();
    const groupMatch = useRouteMatch<{ id: string }>(`${KNOWLEDGE_BASE_ADMIN_PATH}/group/:id`);
    const id = groupMatch?.params.id ?? '';
    const { initializeModal } = useModal();
    const { setFloatingAlert } = useGlobalContext();
    const [sections, setSections] = useState<KnowledgeBaseSection[]>([]);
    const [articles, setArticles] = useState<KnowledgeBaseArticleListItem[]>([]);

    // Fetch the group, its sections and its articles
    const groupParams = useMemo(() => ({ group: id }), [id]);
    const { response: group, refetch: refetchGroup } = useFetchData({ apiUrl: `get_knowledge_base_group_as_superuser/${id}` });
    const { response: sectionResponse, refetch: refetchSections } = useFetchData({ apiUrl: 'get_knowledge_base_section_list_as_superuser', params: groupParams });
    const { response: articleResponse, refetch: refetchArticles } = useFetchData({ apiUrl: 'get_knowledge_base_article_list_as_superuser', params: groupParams });

    // Store the fetched sections and articles in the local state for drag and drop
    useEffect(() => { if (sectionResponse?.results) setSections(sectionResponse.results); }, [sectionResponse]);
    useEffect(() => { if (articleResponse?.results) setArticles(articleResponse.results); }, [articleResponse]);

    // Returns the articles of a section in their ordering
    const articlesOfSection = (sectionId: number | null) => articles
        .filter(article => article.section === sectionId)
        .sort((a, b) => (a.ordering ?? 0) - (b.ordering ?? 0));

    // Opens the form modal to edit the group
    const handleEditGroup = () => {
        initializeModal(
            <KnowledgeBaseGroupForm />,
            { title: t('knowledge_base.group.edit_title'), modalSize: 'small', itemId: Number(id), onSuccess: () => refetchGroup() }
        );
    };

    // Opens the form modal to add a section
    const handleAddSection = () => {
        initializeModal(
            <KnowledgeBaseSectionForm groupId={Number(id)} />,
            { title: t('knowledge_base.section.add_title'), modalSize: 'extra-small', onSuccess: () => refetchSections() }
        );
    };

    // Opens the form modal to edit a section
    const handleEditSection = (section: KnowledgeBaseSection) => {
        initializeModal(
            <KnowledgeBaseSectionForm groupId={Number(id)} />,
            { title: t('knowledge_base.section.edit_title'), modalSize: 'extra-small', itemId: section.id, onSuccess: () => refetchSections() }
        );
    };

    // Opens the delete confirmation of a section, its articles move to the group itself
    const handleDeleteSection = (section: KnowledgeBaseSection) => {
        initializeModal(
            <DeleteModal onConfirmDelete={async () => {
                try {
                    await saveData({ apiUrl: `patch_knowledge_base_section_as_superuser/${section.id}`, method: 'patch', data: { deleted: true } });
                    refetchSections();
                    refetchArticles();
                } catch (error) {
                    setFloatingAlert({ type: 'danger', message: 'knowledge_base.general.save_failed_message' });
                }
            }} />,
            { modalSize: 'extra-small' }
        );
    };

    // Navigates to the editor for a new article in a section
    const handleAddArticle = (sectionId: number | null) => {
        history.push(`${KNOWLEDGE_BASE_ADMIN_PATH}/article/new/${id}${sectionId ? `/${sectionId}` : ''}`);
    };

    // Stores the ordering of the sections after a drag
    const handleSectionDrag = async (result: DropResult) => {
        const reordered = Array.from(sections);
        const [moved] = reordered.splice(result.source.index, 1);
        reordered.splice(result.destination!.index, 0, moved);
        const updated = reordered.map((section, index) => ({ ...section, ordering: index }));
        setSections(updated);
        await saveData({
            apiUrl: 'patch_knowledge_base_section_ordering_as_superuser',
            method: 'patch',
            data: updated.map(section => ({ id: section.id, ordering: section.ordering })),
        });
    };

    // Stores the ordering and section of the articles after a drag
    const handleArticleDrag = async (result: DropResult) => {
        const parseSection = (droppableId: string) => droppableId === `articles-${NO_SECTION_ID}` ? null : Number(droppableId.replace('articles-', ''));
        const sourceSection = parseSection(result.source.droppableId);
        const targetSection = parseSection(result.destination!.droppableId);

        // Take the article out of its source list and put it in the target list
        const sourceList = articlesOfSection(sourceSection);
        const targetList = sourceSection === targetSection ? sourceList : articlesOfSection(targetSection);
        const [moved] = sourceList.splice(result.source.index, 1);
        targetList.splice(result.destination!.index, 0, { ...moved, section: targetSection });

        // Renumber both lists
        const changed = [
            ...sourceList.map((article, index) => ({ ...article, ordering: index })),
            ...(sourceSection === targetSection ? [] : targetList.map((article, index) => ({ ...article, ordering: index, section: targetSection }))),
        ];
        const changedById = new Map(changed.map(article => [article.id, article]));
        setArticles(articles.map(article => changedById.get(article.id) ?? article));
        await saveData({
            apiUrl: 'patch_knowledge_base_article_ordering_as_superuser',
            method: 'patch',
            data: changed.map(article => ({ id: article.id, ordering: article.ordering, section: article.section })),
        });
    };

    // Handles the end of a section or article drag
    const handleDragEnd = async (result: DropResult) => {
        if (!result.destination) return;
        if (result.type === 'ARTICLE' && result.source.droppableId === result.destination.droppableId && result.source.index === result.destination.index) return;
        if (result.type === 'SECTION' && result.source.index === result.destination.index) return;

        try {
            if (result.type === 'SECTION') await handleSectionDrag(result);
            else await handleArticleDrag(result);
        } catch (error) {
            setFloatingAlert({ type: 'danger', message: 'knowledge_base.general.save_failed_message' });
            refetchSections();
            refetchArticles();
        }
    };

    // Renders the article cards of a section as a droppable list
    const renderArticles = (sectionId: number | null) => {
        const sectionArticles = articlesOfSection(sectionId);
        return (
            <StrictModeDroppable droppableId={`articles-${sectionId ?? NO_SECTION_ID}`} type='ARTICLE'>
                {(provided) => (
                    <div className='kb-card-list kb-article-droppable' ref={provided.innerRef} {...provided.droppableProps}>
                        {sectionArticles.map((article, index) => (
                            <Draggable key={article.id} draggableId={`article-${article.id}`} index={index}>
                                {(dragProvided) => (
                                    <div ref={dragProvided.innerRef} {...dragProvided.draggableProps}>
                                        <KnowledgeBaseArticleCard
                                            article={article}
                                            onClick={() => history.push(`${KNOWLEDGE_BASE_ADMIN_PATH}/article/${article.id}`)}
                                            adminActions={{ dragHandleProps: dragProvided.dragHandleProps ?? undefined }} />
                                    </div>
                                )}
                            </Draggable>
                        ))}
                        {provided.placeholder}
                        <button type='button' className='kb-add-article-button' onClick={() => handleAddArticle(sectionId)}>
                            <FontAwesomeIcon icon={faPlus} /> {t('knowledge_base.article.add_button')}
                        </button>
                    </div>
                )}
            </StrictModeDroppable>
        );
    };

    const groupData = group as KnowledgeBaseGroup | null;

    return (
        <div className='container-filled kb-admin-page kb-group-admin-page'>
            <div className='kb-back-link' onClick={() => history.push(KNOWLEDGE_BASE_ADMIN_PATH)}>
                <FontAwesomeIcon icon={faAngleLeft} /> {t('knowledge_base.general.back_to_overview')}
            </div>

            {groupData && (
                <div className='kb-group-header'>
                    <div className='kb-group-icon'><FontAwesomeIcon icon={iconMapping(groupIconKey(groupData.icon))} /></div>
                    <div className='kb-group-header-text'>
                        <h1>{groupData.name}</h1>
                        {groupData.description && <p>{groupData.description}</p>}
                    </div>
                    <div className='kb-admin-toolbar-buttons'>
                        <SecondaryButton label='knowledge_base.group.edit_button' size='small' icon={<FontAwesomeIcon icon={faPen} />} onClick={handleEditGroup} />
                        <PrimaryButton label='knowledge_base.section.add_button' size='small' icon={<FontAwesomeIcon icon={faPlus} />} onClick={handleAddSection} />
                    </div>
                </div>
            )}

            <DragDropContext onDragEnd={handleDragEnd}>
                {/* Articles without a section */}
                <div className='kb-section'>
                    {articlesOfSection(null).length > 0 && <h2 className='kb-section-title kb-section-title-none'>{t('knowledge_base.section.without_section')}</h2>}
                    {renderArticles(null)}
                </div>

                {/* The sections with their articles */}
                <StrictModeDroppable droppableId='kb-sections' type='SECTION'>
                    {(provided) => (
                        <div ref={provided.innerRef} {...provided.droppableProps}>
                            {sections.map((section, index) => (
                                <Draggable key={section.id} draggableId={`section-${section.id}`} index={index} isDragDisabled={sections.length <= 1}>
                                    {(dragProvided) => (
                                        <div className='kb-section' ref={dragProvided.innerRef} {...dragProvided.draggableProps}>
                                            <div className='kb-section-header'>
                                                <div className='kb-section-drag-handle' {...dragProvided.dragHandleProps}>
                                                    <FontAwesomeIcon icon={faGripLines} />
                                                </div>
                                                <h2 className='kb-section-title'>{section.name}</h2>
                                                <div className='kb-card-actions'>
                                                    <button type='button' className='kb-icon-button' onClick={() => handleEditSection(section)} title={t('general.edit')}>
                                                        <FontAwesomeIcon icon={faPen} />
                                                    </button>
                                                    <button type='button' className='kb-icon-button' onClick={() => handleDeleteSection(section)} title={t('general.delete')}>
                                                        <FontAwesomeIcon icon={faTrash} />
                                                    </button>
                                                </div>
                                            </div>
                                            {renderArticles(section.id)}
                                        </div>
                                    )}
                                </Draggable>
                            ))}
                            {provided.placeholder}
                        </div>
                    )}
                </StrictModeDroppable>
            </DragDropContext>

            {sections.length === 0 && articles.length === 0 && (
                <div className='kb-empty-state'>{t('knowledge_base.section.empty_state')}</div>
            )}
        </div>
    );
};

export default KnowledgeBaseGroupAdminPage;
